import productApiFactory from '../../modules/product/productApi'

/* Utils */
import {
  generateDefaultAsyncWatcher,
  generateDefaultAsyncSaga,
} from '../core/reduxUtils'

/* Actions */
import {
  getCardAndInstallmentActionCreators,
  getProductActionCreators,
  getProductReturnPolicyActionCreators,
  getSizeChartActionCreators,
  getSplitProductVariantsActionCreators,
  getCampaignsPdpDashboardActionCreators,
  getDueDateActionCreators,
  getDueDateProductsActionCreators,
  getProductListingsActionCreators,
  getDesktopTabsActionCreators,
  getVasActionCreators,
  getOtherSellerCampaignActionCreators,
  getOtherSellerCouponActionCreators,
  getComparedProductsActionCreators,
  getBasicProductActionCreators
} from '../actions/productActions'

function productSagasFactory({storefrontApiClient}) {
  const productApi = productApiFactory(storefrontApiClient);

  const getSplitProductVariantsSaga = generateDefaultAsyncSaga(
    productApi.getSplitProductVariants,
    getSplitProductVariantsActionCreators,
    payload => [
      payload.sku,
      payload.params,
    ]
  );

  const getProductSaga = generateDefaultAsyncSaga(
    productApi.getProduct,
    getProductActionCreators,
    payload => [
      payload.sku,
      payload.params,
      payload.isProductId
    ]
  );

  const getBasicProductSaga = generateDefaultAsyncSaga(
    productApi.getBasicProduct,
    getBasicProductActionCreators,
    payload => [
      payload.sku,
      payload.params,
      payload.isProductId
    ]
  );

  const getVasSaga = generateDefaultAsyncSaga(
    productApi.getVas,
    getVasActionCreators,
    payload => [
      payload
    ]
  );

  const getProductReturnPolicySaga = generateDefaultAsyncSaga(
    productApi.getProductReturnPolicy,
    getProductReturnPolicyActionCreators,
    payload => [
      payload.productSku,
      payload.params
    ]
  );

  const getSizeChartSaga = generateDefaultAsyncSaga(
    productApi.getSizeChart,
    getSizeChartActionCreators,
    payload => [
      payload.params
    ]
  );

  const getCardAndInstallmentSaga = generateDefaultAsyncSaga(
    productApi.getCardAndInstallment,
    getCardAndInstallmentActionCreators,
    payload => [
      payload.params
    ]
  );

  const getCampaignsPdpDashboardSaga = generateDefaultAsyncSaga(
    productApi.getCampaignsPdpDashboard,
    getCampaignsPdpDashboardActionCreators,
    payload => [
      payload.params,
    ]
  );

  const getDueDateSaga = generateDefaultAsyncSaga(
    productApi.getDueDate,
    getDueDateActionCreators,
    payload => [
      payload.params,
    ]
  );

  const getDueDateProductsSaga = generateDefaultAsyncSaga(
    productApi.getDueDateProducts,
    getDueDateProductsActionCreators,
    payload => [
      payload.params,
    ]
  );

  const getProductListingsSaga = generateDefaultAsyncSaga(
    productApi.getProductListings,
    getProductListingsActionCreators,
    payload => [
      payload.sku,
      payload.params
    ]
  );

  const getDesktopTabSaga = generateDefaultAsyncSaga(
    () => {
      return Promise.resolve()
    },
    getDesktopTabsActionCreators,
    payload => [
      payload.index
    ]
  );

  const getOtherSellerCampaignSaga = generateDefaultAsyncSaga(
    productApi.getOtherSellerCampaigns,
    getOtherSellerCampaignActionCreators,
    payload => [
      payload.params,
    ]
  );

  const getOtherSellerCouponSaga = generateDefaultAsyncSaga(
    productApi.getOtherSellerCoupon,
    getOtherSellerCouponActionCreators,
    payload => [
      payload.params,
    ]
  );

  const getComparedProductSaga = generateDefaultAsyncSaga(
    productApi.getComparedProducts,
    getComparedProductsActionCreators,
    payload => [
      payload.params,
    ]
  );

  const watchGetCampaignsPdpDashboard = generateDefaultAsyncWatcher(
    'GET_CAMPAIGNS_PDP_DASHBOARD',
    getCampaignsPdpDashboardSaga
  );

  const watchGetSplitProductVariants = generateDefaultAsyncWatcher(
    'GET_SPLIT_PRODUCT_VARIANTS',
    getSplitProductVariantsSaga,
  );

  const watchGetProduct = generateDefaultAsyncWatcher(
    'GET_PRODUCT',
    getProductSaga,
  );

  const watchGetBasicProduct = generateDefaultAsyncWatcher(
    'GET_BASIC_PRODUCT',
    getBasicProductSaga,
  );

  const watchGetVas = generateDefaultAsyncWatcher(
    'GET_VAS',
    getVasSaga,
  );

  const watchGetProductReturnPolicy = generateDefaultAsyncWatcher(
    'GET_PRODUCT_RETURN_POLICY',
    getProductReturnPolicySaga,
  );

  const watchGetSizeChart = generateDefaultAsyncWatcher(
    'GET_SIZE_CHART',
    getSizeChartSaga,
  );

  const watchGetCardAndInstallment = generateDefaultAsyncWatcher(
    'GET_CARD_AND_INSTALLMENT',
    getCardAndInstallmentSaga,
  );

  const watchDueDate = generateDefaultAsyncWatcher(
    'GET_DUE_DATE',
    getDueDateSaga,
  );

  const watchDueDateProducts = generateDefaultAsyncWatcher(
    'GET_DUE_DATE_PRODUCTS',
    getDueDateProductsSaga,
  );

  const watchGetProductListings = generateDefaultAsyncWatcher(
    'GET_PRODUCT_LISTINGS',
    getProductListingsSaga,
  );

  const watchGetDesktopTab = generateDefaultAsyncWatcher(
    'GET_DESKTOP_TABS',
    getDesktopTabSaga,
  );

  const watchGetOtherSellerCampaigns = generateDefaultAsyncWatcher(
    'GET_OTHER_SELLER_CAMPAIGN',
    getOtherSellerCampaignSaga
  );

  const watchGetOtherSellerCoupon = generateDefaultAsyncWatcher(
    'GET_OTHER_SELLER_COUPON',
    getOtherSellerCouponSaga
  );

  const watchGetComparedProducts = generateDefaultAsyncWatcher(
    'GET_COMPARED_PRODUCTS',
    getComparedProductSaga
  );

  const productSagas = [
    watchGetSplitProductVariants(),
    watchGetProduct(),
    watchGetProductReturnPolicy(),
    watchGetSizeChart(),
    watchGetCardAndInstallment(),
    watchGetCampaignsPdpDashboard(),
    watchDueDate(),
    watchDueDateProducts(),
    watchGetProductListings(),
    watchGetDesktopTab(),
    watchGetVas(),
    watchGetOtherSellerCampaigns(),
    watchGetOtherSellerCoupon(),
    watchGetComparedProducts(),
    watchGetBasicProduct()
  ]

  return productSagas
}

export default productSagasFactory
